import React, { useCallback } from 'react';
import {
  Button,
  ChevronRightIcon,
  Fact,
  LedgerHoverCard,
  LedgerRow,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { LabourItemDefinition } from '../../api/labourItemsApi';

interface LabourItemListItemProps {
  item: LabourItemDefinition;
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  onItemClick: (id: string) => void;
  disabled?: boolean;
}

export const LabourItemListItem = React.memo(function LabourItemListItem({
  item,
  columns,
  collapse,
  onItemClick,
  disabled,
}: LabourItemListItemProps) {
  const handleClick = useCallback(() => {
    if (!disabled) {
      onItemClick(item.id);
    }
  }, [disabled, item.id, onItemClick]);

  const groupLabel =
    item.groupCount === 0
      ? 'Not assigned'
      : `${item.groupCount} group${item.groupCount === 1 ? '' : 's'}`;

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      title={item.name}
      cells={{ usedBy: groupLabel }}
      meta={groupLabel}
      showValues={false}
      dimmed={disabled}
      hoverCard={
        <LedgerHoverCard
          title={item.name}
          kind="Labour item"
          onOpenDetails={handleClick}
          openDetailsLabel="Open item details"
        >
          <Fact label="Used by">{groupLabel}</Fact>
        </LedgerHoverCard>
      }
      actions={
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          disabled={disabled}
          aria-label={`Open ${item.name}`}
          className="size-8"
        >
          <ChevronRightIcon aria-hidden="true" className="size-4" />
        </Button>
      }
      onClick={disabled ? undefined : handleClick}
      aria-label={`Open labour item ${item.name}`}
      data-testid="labour-item-row"
    />
  );
});
